import { STALE_ARTEFACTS_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { AlertTriangle } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "urql";
import { RegenerateModal, type StaleItem } from "./RegenerateModal";

interface Props {
  campaignId: string;
  /** Which artefact kinds this stage is responsible for. Banner shows only
   * matching stale items. */
  kinds: string[];
  /** Text override; defaults to "{count} artefact(s) are out of date". */
  message?: string;
}

export function StalenessBanner({ campaignId, kinds, message }: Props) {
  const [open, setOpen] = useState(false);

  const [{ data, fetching }, reexecute] = useQuery({
    query: STALE_ARTEFACTS_QUERY,
    variables: { id: campaignId },
  });
  // Regenerate jobs broadcast on CampaignChannel as their artefact versions
  // commit. Refetch so the banner clears the moment the run finishes; without
  // this the urql doc cache keeps showing the pre-regenerate snapshot.
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const matchingItems = useMemo(() => {
    const all = (data?.campaign?.staleArtefacts ?? []) as StaleItem[];
    const set = new Set(kinds);
    return all.filter((i) => set.has(i.kind));
  }, [data, kinds]);

  if (fetching || matchingItems.length === 0) return null;

  const count = matchingItems.length;
  const text = message ?? `${count} artefact${count === 1 ? " is" : "s are"} out of date.`;

  return (
    <>
      <div
        data-testid="staleness-banner"
        className="mb-4 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} />
          <span>{text}</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          Review and regenerate
        </button>
      </div>
      <RegenerateModal
        open={open}
        campaignId={campaignId}
        kinds={kinds}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
